import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  FileText,
  AlertTriangle,
  CheckCircle,
  X,
  Upload,
  Loader2,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { statutoryComplianceService } from '../../../api/services/statutoryComplianceService';
import { policyService } from '../../../api/services/complianceService';
import { toast } from '../../../utils/toast';
import { formatDate } from '../../../utils/stringUtils';
import ExportModal from '../../../components/common/ExportModal';
import { exportToCSV } from '../../../utils/exportUtils';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import useUrlTableState from '../../../hooks/useUrlTableState';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import { API_BASE_URL } from '../../../api/config';

const StatutoryCompliance = ({ onBack, userRole, initialMode, id, onClose, isDrawer }) => {
  const queryClient = useQueryClient();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterExpired: 'all',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: complianceFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateComplianceFilter,
    updatePage: updateCompliancePage,
    updateLimit: updateComplianceLimit,
    resetFilters: resetComplianceFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;
  const debouncedSearchTerm = searchTerm; // Hook handles debouncing internally

  const filterExpired = complianceFilters.filterExpired || 'all';
  const setFilterExpired = useCallback((status) => {
    updateComplianceFilter({ filterExpired: status });
  }, [updateComplianceFilter]);

  const startDate = complianceFilters.filterStartDate || '';
  const endDate = complianceFilters.filterEndDate || '';

  const setDateRange = useCallback((range) => {
    updateComplianceFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateComplianceFilter]);

  const setStartDate = useCallback((date) => {
    updateComplianceFilter({ filterStartDate: date });
  }, [updateComplianceFilter]);

  const setEndDate = useCallback((date) => {
    updateComplianceFilter({ filterEndDate: date });
  }, [updateComplianceFilter]);

  const page = complianceFilters.page;
  const pageSize = complianceFilters.limit;

  const setPage = useCallback((p) => {
    updateCompliancePage(p);
  }, [updateCompliancePage]);

  const setPageSize = useCallback((l) => {
    updateComplianceLimit(l);
  }, [updateComplianceLimit]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [formData, setFormData] = useState({
    documentType: '',
    documentName: '',
    documentNumber: '',
    issuedDate: '',
    expiryDate: '',
    fileUrl: '',
    filePublicId: '',
    renewalReminderDays: 15
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [deletingFile, setDeletingFile] = useState(false);
  const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCompliance, setSelectedCompliance] = useState(null);
  const fileInputRef = useRef(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRowsData, setSelectedRowsData] = useState({});
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);


  // Character limits for fields
  const FIELD_LIMITS = {
    documentType: 50,
    documentName: 100,
    documentNumber: 50,
    search: 100
  };

  // Helper function to validate and format text input (alphanumeric, spaces, common symbols)
  const handleTextInput = (value, fieldName, maxLength) => {
    // Allow alphanumeric, spaces, and common document symbols (/, -, _, ., ( ), ,)
    let cleaned = value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '');
    // Enforce max length
    if (maxLength && cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength);
    }
    return cleaned;
  };

  // Helper function to validate and format number input (positive numbers only)
  const handleNumberInput = (value, maxValue = null) => {
    // Remove any non-numeric characters except decimal point (if needed)
    let cleaned = value.replace(/[^0-9]/g, '');
    // Convert to number and check if it's valid
    const numValue = cleaned === '' ? '' : parseInt(cleaned, 10);
    // Enforce max value if provided
    if (maxValue !== null && numValue !== '' && numValue > maxValue) {
      return maxValue.toString();
    }
    return cleaned === '' ? '' : numValue.toString();
  };

  // Helper function to validate search input (alphanumeric, spaces, common symbols)
  const handleSearchInput = (value, maxLength) => {
    // Allow alphanumeric, spaces, and common search/document symbols (@, ., /, -, _, ( ))
    let cleaned = value.replace(/[^a-zA-Z0-9\s,.@\/\-_\(\)\[\]#:&?!]/g, '');
    // Enforce max length
    if (maxLength && cleaned.length > maxLength) {
      cleaned = cleaned.substring(0, maxLength);
    }
    return cleaned;
  };

  // Helper function to count character types
  const countCharacters = (text) => {
    const letters = (text.match(/[a-zA-Z]/g) || []).length;
    const numbers = (text.match(/[0-9]/g) || []).length;
    const spaces = (text.match(/\s/g) || []).length;
    const special = text.length - letters - numbers - spaces;
    return { letters, numbers, spaces, special, total: text.length };
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 50) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Debounce search term - useUrlTableState handles debouncing internally

  // React Query: Fetch compliances
  // When filtering, fetch all items; otherwise use pagination
  const {
    data: compliancesResponse,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['statutory-compliances', page, pageSize, searchTerm, filterExpired, startDate, endDate],
    queryFn: async () => {
      const response = await statutoryComplianceService.getCompliances({
        page,
        limit: pageSize,
        search: searchTerm || undefined,
        filterStatus: filterExpired !== 'all' ? filterExpired : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      return response;
    },
    staleTime: 0,
  });

  const compliances = useMemo(() => {
    if (!compliancesResponse) return [];
    const payload = compliancesResponse?.data || compliancesResponse;
    return Array.isArray(payload?.items) ? payload.items : [];
  }, [compliancesResponse]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedCompliances = useMemo(() => {
    if (!sortField) return compliances;

    return [...compliances].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special mapping for display fields
      if (sortField === 'status') {
        aValue = a.statusText || 'Active';
        bValue = b.statusText || 'Active';
      }

      // Handle numeric values (none in current table but good practice)
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle dates
      if ((sortField === 'issuedDate' || sortField === 'expiryDate' || sortField === 'createdAt') && aValue && bValue) {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle strings
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();

      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [compliances, sortField, sortDirection]);

  const pagination = useMemo(() => {
    const payload = compliancesResponse?.data || compliancesResponse;
    return payload?.pagination || {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false
    };
  }, [compliancesResponse]);

  // Combined results for selection lookup (though with server pagination, we only select from current page)
  const allCompliances = compliances;

  useEffect(() => {
    if (id && initialMode === "view") {
      const compliance = compliances.find(c => (c.id || c._id) === id);
      if (compliance) {
        setSelectedCompliance(compliance);
        setShowViewModal(true);
      }
    }
  }, [id, initialMode, compliances.length]);

  // Extract pagination info (use filtered count)
  const { totalItems, totalPages } = useMemo(() => {
    return {
      totalItems: pagination.totalItems,
      totalPages: pagination.totalPages,
    };
  }, [pagination]);

  // React Query: Create compliance mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      // In compliance with immediate upload, we send regular JSON if file is already uploaded to Cloudinary
      const submitPayload = {
        documentType: data.documentType,
        documentName: data.documentName,
        documentNumber: data.documentNumber,
        issuedDate: data.issuedDate ? new Date(data.issuedDate).toISOString() : '',
        expiryDate: data.expiryDate ? new Date(data.expiryDate).toISOString() : '',
        renewalReminderDays: data.renewalReminderDays,
        fileUrl: data.fileUrl,
        filePublicId: data.filePublicId
      };

      const response = await statutoryComplianceService.createCompliance(submitPayload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statutory-compliances'] });
      toast.success('Statutory compliance created successfully');
      handleCloseForm();
    },
    onError: (error) => {
      const errorMessage = error.message || 'Failed to create compliance';
      toast.error(errorMessage);

      // If error message indicates duplicate document number, set form error
      if (errorMessage.toLowerCase().includes('document number') && errorMessage.toLowerCase().includes('exists')) {
        setFormErrors(prev => ({ ...prev, documentNumber: errorMessage }));
        setTouched(prev => ({ ...prev, documentNumber: true }));
      }
    }
  });

  // React Query: Update compliance mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const submitPayload = {
        documentType: data.documentType,
        documentName: data.documentName,
        documentNumber: data.documentNumber,
        issuedDate: data.issuedDate ? new Date(data.issuedDate).toISOString() : '',
        expiryDate: data.expiryDate ? new Date(data.expiryDate).toISOString() : '',
        renewalReminderDays: data.renewalReminderDays,
        fileUrl: data.fileUrl,
        filePublicId: data.filePublicId
      };

      const response = await statutoryComplianceService.updateCompliance(id, submitPayload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statutory-compliances'] });
      toast.success('Statutory compliance updated successfully');
      handleCloseForm();
    },
    onError: (error) => {
      const errorMessage = error.message || 'Failed to update compliance';
      toast.error(errorMessage);

      // If error message indicates duplicate document number, set form error
      if (errorMessage.toLowerCase().includes('document number') && errorMessage.toLowerCase().includes('exists')) {
        setFormErrors(prev => ({ ...prev, documentNumber: errorMessage }));
        setTouched(prev => ({ ...prev, documentNumber: true }));
      }
    }
  });

  // React Query: Delete compliance mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return await statutoryComplianceService.deleteCompliance(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['statutory-compliances'] });
      toast.success('Statutory compliance deleted successfully');
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete compliance');
    }
  });


  // Format date for input field (yyyy-mm-dd for date input)
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return (
      formData.documentType.trim() !== '' &&
      formData.documentName.trim() !== '' &&
      formData.documentNumber.trim() !== '' &&
      formData.issuedDate !== '' &&
      formData.expiryDate !== '' &&
      (formData.fileUrl) &&
      !formErrors.documentType &&
      !formErrors.documentName &&
      !formErrors.documentNumber &&
      !formErrors.issuedDate &&
      !formErrors.expiryDate &&
      !formErrors.file
    );
  }, [formData, formErrors]);

  // Validate form reactively
  useEffect(() => {
    if (showForm) {
      validateForm();
    }
  }, [formData, showForm]);

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.documentType.trim()) {
      errors.documentType = 'Document type is required';
    } else if (formData.documentType.length > FIELD_LIMITS.documentType) {
      errors.documentType = `Document type must be ${FIELD_LIMITS.documentType} characters or less`;
    }

    if (!formData.documentName.trim()) {
      errors.documentName = 'Document name is required';
    } else if (formData.documentName.length > FIELD_LIMITS.documentName) {
      errors.documentName = `Document name must be ${FIELD_LIMITS.documentName} characters or less`;
    }

    if (!formData.documentNumber.trim()) {
      errors.documentNumber = 'Document number is required';
    } else if (formData.documentNumber.length > FIELD_LIMITS.documentNumber) {
      errors.documentNumber = `Document number must be ${FIELD_LIMITS.documentNumber} characters or less`;
    }

    if (!formData.issuedDate) {
      errors.issuedDate = 'Issued date is required';
    }

    if (!formData.expiryDate) {
      errors.expiryDate = 'Expiry date is required';
    }

    // Validate expiry date is greater than issued date
    if (formData.issuedDate && formData.expiryDate) {
      const issued = new Date(formData.issuedDate);
      const expiry = new Date(formData.expiryDate);

      // Set time to midnight for accurate comparison
      issued.setHours(0, 0, 0, 0);
      expiry.setHours(0, 0, 0, 0);

      if (expiry <= issued) {
        errors.expiryDate = 'Expiry date must be greater than issued date';
      }
    }

    if (!selectedFile && !existingFileUrl && !formData.fileUrl) {
      errors.file = 'File is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    const submitData = {
      ...formData,
      file: selectedFile
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const ensureFilenameHasExtension = (filename, mimeType = '') => {
    const base = String(filename || 'document').trim() || 'document';
    if (/\.[a-zA-Z0-9]+$/.test(base)) return base;

    const type = String(mimeType || '').toLowerCase();
    if (type === 'application/pdf') return `${base}.pdf`;
    if (type === 'application/msword') return `${base}.doc`;
    if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return `${base}.docx`;
    if (type === 'application/vnd.ms-powerpoint') return `${base}.ppt`;
    if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return `${base}.pptx`;
    if (type === 'image/jpeg') return `${base}.jpg`;
    if (type === 'image/png') return `${base}.png`;
    if (type === 'image/webp') return `${base}.webp`;
    return `${base}.pdf`;
  };

  const extractObjectKeyFromUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    try {
      const parsed = new URL(url);
      const host = String(parsed.hostname || '').toLowerCase();
      const pathname = decodeURIComponent(parsed.pathname || '').replace(/^\/+/, '');

      if (!pathname) return '';

      if (/\.s3[.-][a-z0-9-]+\.amazonaws\.com$/.test(host) || host.endsWith('.s3.amazonaws.com')) {
        return pathname;
      }

      if (host.startsWith('s3.') || host === 's3.amazonaws.com') {
        const segments = pathname.split('/');
        if (segments.length > 1) return segments.slice(1).join('/');
      }

      return pathname;
    } catch {
      return '';
    }
  };

  const parseFilenameFromContentDisposition = (contentDisposition = '') => {
    const utf8Match = String(contentDisposition).match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]).replace(/(^"|"$)/g, '');
      } catch {
        return utf8Match[1].replace(/(^"|"$)/g, '');
      }
    }

    const simpleMatch = String(contentDisposition).match(/filename=([^;]+)/i);
    return simpleMatch?.[1] ? simpleMatch[1].trim().replace(/(^"|"$)/g, '') : '';
  };

  const handleDownloadComplianceFile = async (compliance) => {
    const fileUrl = compliance?.fileUrl || '';
    const publicId = String(compliance?.filePublicId || extractObjectKeyFromUrl(fileUrl) || '').trim();

    if (!publicId) {
      toast.error('File key not found');
      return;
    }

    const fallbackFilename = ensureFilenameHasExtension(
      compliance?.fileOriginalName || compliance?.documentName || compliance?.documentType || 'document',
      compliance?.fileMimeType || '',
    );

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${API_BASE_URL}/upload/cloudinary/download?public_id=${encodeURIComponent(publicId)}&filename=${encodeURIComponent(fallbackFilename)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const headerFilename = parseFilenameFromContentDisposition(response.headers.get('content-disposition') || '');
      const resolvedFilename = ensureFilenameHasExtension(
        headerFilename || fallbackFilename,
        blob.type || compliance?.fileMimeType || '',
      );

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = resolvedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      toast.error(error.message || 'Failed to download file');
    }
  };

  // Handle edit
  const handleEdit = (compliance) => {
    setEditingId(compliance.id);
    setFormData({
      documentType: compliance.documentType || '',
      documentName: compliance.documentName || '',
      documentNumber: compliance.documentNumber || '',
      issuedDate: formatDateForInput(compliance.issuedDate),
      expiryDate: formatDateForInput(compliance.expiryDate),
      fileUrl: compliance.fileUrl || '',
      filePublicId: compliance.filePublicId || '',
      renewalReminderDays: compliance.renewalReminderDays || 15
    });
    setExistingFileUrl(compliance.fileUrl);
    setSelectedFile(null);
    setTouched({});
    setShowForm(true);
  };

  // Handle delete - show confirmation dialog first
  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  };

  // Cancel delete action
  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const selectedIds = Object.keys(selectedRowsData);
    if (selectedIds.length === 0) return;

    setBulkDeleteLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of selectedIds) {
      try {
        await statutoryComplianceService.deleteCompliance(id);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRowsData({});

    if (successCount > 0) {
      toast.success(`${successCount} record(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['statutory-compliances'] });
    } else {
      toast.error('Failed to delete selected records');
    }
  };

  // Handle view details
  const handleView = (compliance) => {
    setSelectedCompliance(compliance);
    setShowViewModal(true);
  };

  // Handle close view modal
  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedCompliance(null);
  };

  // Handle close form
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      documentType: '',
      documentName: '',
      documentNumber: '',
      issuedDate: '',
      expiryDate: '',
      fileUrl: '',
      filePublicId: '',
      renewalReminderDays: 15
    });
    setFormErrors({});
    setTouched({});
    setSelectedFile(null);
    setExistingFileUrl(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file change with immediate Cloudinary upload
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0] || e.dataTransfer?.files?.[0];
    if (file) {
      // 50MB limit
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File size must be less than 50MB");
        return;
      }

      setUploadingFile(true);
      try {
        const result = await policyService.uploadCloudinary(file, 'hrms-statutory-compliance');
        setFormData({
          ...formData,
          fileUrl: result.secure_url || result.url,
          filePublicId: result.public_id
        });
        toast.success("File uploaded successfully");
        if (formErrors.file) {
          setFormErrors({ ...formErrors, file: '' });
        }
      } catch (error) {
        toast.error("Failed to upload file. Please try again.");
      } finally {
        setUploadingFile(false);
      }
    }
  };

  // Handle remove file from Cloudinary before saving
  const handleRemoveFile = async () => {
    if (!formData.filePublicId) {
      setFormData({ ...formData, fileUrl: '', filePublicId: '' });
      return;
    }

    setDeletingFile(true);
    try {
      await policyService.deleteCloudinary(formData.filePublicId, 'raw');
      setFormData({
        ...formData,
        fileUrl: '',
        filePublicId: ''
      });
      toast.success("File removed successfully");
      setShowFileDeleteConfirm(false);
    } catch (error) {
      toast.error("Failed to remove file from server. Please try again.");
    } finally {
      setDeletingFile(false);
    }
  };

  // Page reset is handled by useUrlTableState

  useEffect(() => {
    setSortField(null);
    setSortDirection('asc');
  }, [filterExpired]);

  // Map compliance for export
  const mapComplianceForExport = (c) => ({
    'Document Type': c.documentType || 'N/A',
    'Document Name': c.documentName || 'N/A',
    'Document Number': c.documentNumber || 'N/A',
    'Issued Date': c.issuedDate ? formatDate(c.issuedDate, { includeTime: false }) : 'N/A',
    'Expiry Date': c.expiryDate ? formatDate(c.expiryDate, { includeTime: false }) : 'N/A',
    'Status': c.statusText || 'Active',
    'Created At': c.createdAt ? formatDate(c.createdAt, { includeTime: true }) : 'N/A'
  });

  // Handle row selection
  const handleSelectRow = (item) => {
    const id = item.id || item._id;
    setSelectedRowsData(prev => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = item;
      }
      return next;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRowsData(prev => {
        const next = { ...prev };
        sortedCompliances.forEach(c => {
          const id = c.id || c._id;
          next[id] = c;
        });
        return next;
      });
    } else {
      // User requested to reset selection when unchecking header box
      setSelectedRowsData({});
    }
  };

  // Export handlers
  const handleExportPerPage = () => {
    if (compliances.length === 0) {
      toast.warning("No data found on current page");
      return;
    }
    const exportData = sortedCompliances.map(mapComplianceForExport);
    exportToCSV(exportData, 'statutory_compliance_page', { title: 'Statutory Compliance Export', type: 'Current Page' });
    toast.success("✅ Page export generated!");
  };

  const handleExportSelected = () => {
    const selectedItems = Object.values(selectedRowsData);
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one record to export");
      return;
    }
    const exportData = selectedItems.map(mapComplianceForExport);
    exportToCSV(exportData, 'statutory_compliance_selected', { title: 'Statutory Compliance Export', type: 'Selected Records' });
    toast.success(`✅ ${selectedItems.length} selected records exported!`);
    setSelectedRowsData({}); // Reset after export as is common practice
  };

  const handleExportRange = async (startDate, endDate) => {
    try {
      setExportLoading(true);
      const params = {
        search: searchTerm,
        filterStatus: filterExpired,
        startDate,
        endDate
      };

      const response = await statutoryComplianceService.fetchExportData(params);
      const data = response?.data || response || [];

      if (!Array.isArray(data) || data.length === 0) {
        toast.warning("No data found for the selected range");
        return;
      }

      const exportData = data.map(mapComplianceForExport);
      exportToCSV(exportData, 'statutory_compliance_range', { title: 'Statutory Compliance Export', type: `Date Range: ${startDate} to ${endDate}` });

      setShowExportModal(false);
      toast.success("✅ Range export generated!");
    } catch (error) {
      toast.error("Failed to export compliance data");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    try {
      setExportLoading(true);
      const params = {
        search: searchTerm,
        filterStatus: filterExpired
      };

      const response = await statutoryComplianceService.fetchExportData(params);
      const data = response?.data || response || [];

      if (!Array.isArray(data) || data.length === 0) {
        toast.warning("No data found to export");
        return;
      }

      const exportData = data.map(mapComplianceForExport);
      exportToCSV(exportData, 'statutory_compliance_all', { title: 'Statutory Compliance Export', type: 'All Records' });

      setShowExportModal(false);
      toast.success("✅ All data export generated!");
    } catch (error) {
      toast.error("Failed to export all compliance data");
    } finally {
      setExportLoading(false);
    }
  };

  const loading = isLoading || isFetching;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* File Delete Confirmation Dialog */}
      {showFileDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              Delete Document?
            </h3>
            <p className="text-gray-600 mb-6">
              This will permanently remove the document from the server.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowFileDeleteConfirm(false)}
                disabled={deletingFile}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleRemoveFile}
                disabled={deletingFile}
              >
                {deletingFile ? (
                  <div className="flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </div>
                ) : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm Action</h3>
              </div>

              <p className="text-gray-600 mb-6">Are you sure you want to delete this compliance document?</p>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={cancelDelete}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    'Yes, Delete'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Delete Confirmation Dialog */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Deletion</h3>
              </div>

              <p className="text-gray-600 mb-2">
                Are you sure you want to delete <span className="font-bold text-red-600">{Object.keys(selectedRowsData).length}</span> selected compliance documents?
              </p>
              <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                >
                  {bulkDeleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    `Yes, Delete ${Object.keys(selectedRowsData).length} Records`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Statutory Compliance
              </h1>
              <p className="text-gray-600">
                Manage statutory compliance documents and renewals
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {Object.keys(selectedRowsData).length > 0 && (
                <PermissionGate permission="compliance_policy" action="delete">
                  <Button
                    variant="danger"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    loading={bulkDeleteLoading}
                  >
                    Delete Selected ({Object.keys(selectedRowsData).length})
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permission="compliance_policy" action="read">
                {Object.keys(selectedRowsData).length > 0 ? (
                  <Button
                    onClick={handleExportSelected}
                    className="flex items-center space-x-2 bg-agrosaf-green hover:bg-agrosaf-green/90"
                  >
                    <Download className="h-4 w-4" />
                    <span>Export Selected ({Object.keys(selectedRowsData).length})</span>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowExportModal(true)}
                    className="flex items-center space-x-2"
                    disabled={exportLoading}
                  >
                    {exportLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-[#0F2942]" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span>Export</span>
                  </Button>
                )}
              </PermissionGate>
              <PermissionGate permission="compliance_policy" action="create">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkUpload(true)}
                  className="btn-outline flex items-center space-x-2"
                >
                  <Upload className="h-4 w-4" />
                  <span>Bulk Upload</span>
                </Button>
              </PermissionGate>
              <PermissionGate permission="compliance_policy" action="create">
                <button
                  onClick={() => setShowForm(true)}
                  className="btn-cancel flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Compliance</span>
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search Bar */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by document type, name, or number..."
              value={searchTerm}
              onChange={(e) => {
                const cleaned = handleSearchInput(e.target.value, FIELD_LIMITS.search);
                setSearchTerm(cleaned);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
              autoFocus
              maxLength={FIELD_LIMITS.search}
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  // Maintain focus on input
                  const input = document.activeElement;
                  if (input && input.tagName === 'INPUT') {
                    setTimeout(() => input.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterExpired}
              onChange={(e) => setFilterExpired(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            >
              <option value="all">All Status</option>
              <option value="active">Active </option>
              <option value="expired">Expired </option>
              <option value="expiring">Expiring soon </option>
            </select>
          </div>

          {/* Issued Date Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={setDateRange}
              containerClassName="flex items-center space-x-2"
              inputClassName="text-sm w-36 cursor-pointer px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-brand/60"
            />
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || filterExpired !== 'all' || startDate || endDate) && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  resetComplianceFilters();
                  setSortField(null);
                  setSortDirection('asc');
                }}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={sortedCompliances.length > 0 && sortedCompliances.every(c => !!selectedRowsData[c.id || c._id])}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-1"
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('documentType')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Document Type</span>
                    {sortField === 'documentType' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('documentName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Document Name</span>
                    {sortField === 'documentName' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('documentNumber')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Document Number</span>
                    {sortField === 'documentNumber' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('issuedDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Issued Date</span>
                    {sortField === 'issuedDate' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('expiryDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Expiry Date</span>
                    {sortField === 'expiryDate' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created</span>
                    {sortField === 'createdAt' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-[#0F2942]" />
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : compliances.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12">
                    <div className="text-center">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No compliance documents found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm || filterExpired !== "all"
                          ? "Try adjusting your search or filter criteria."
                          : "Get started by creating your first compliance document."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedCompliances.map((compliance) => {
                  return (
                    <tr
                      key={compliance.id || compliance._id}
                      className={`hover:bg-gray-50 ${compliance.color || ''}`}
                    >
                      <td className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          checked={!!selectedRowsData[compliance.id || compliance._id]}
                          onChange={() => handleSelectRow(compliance)}
                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-brand focus:ring-1"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 capitalize max-w-[250px]">
                        <div className="truncate" title={compliance.documentType || 'N/A'}>
                          {compliance.documentType || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 capitalize max-w-[250px]">
                        <div className="truncate" title={compliance.documentName || 'N/A'}>
                          {compliance.documentName || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 uppercase max-w-[250px]">
                        <div className="truncate" title={compliance.documentNumber || 'N/A'}>
                          {compliance.documentNumber || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {compliance.issuedDate ? formatDate(compliance.issuedDate, { includeTime: false }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {compliance.expiryDate ? formatDate(compliance.expiryDate, { includeTime: false }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${compliance.badgeColor || 'bg-green-100 text-green-800'}`}>
                          {compliance.statusText || 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {compliance.createdAt ? formatDate(compliance.createdAt, { includeTime: true }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(compliance)}
                            className="text-purple-600 hover:text-purple-700"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {compliance.fileUrl && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadComplianceFile(compliance)}
                              className="text-green-600 hover:text-green-700"
                              title="Download File"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          )}
                          <PermissionGate permission="compliance_policy" action="update">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(compliance)}
                              className="text-[#0F2942] hover:text-[#0B2033]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="compliance_policy" action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(compliance.id)}
                              className="text-red-600 hover:text-red-700"
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {/* Pagination */}
          <PaginationControls
            currentPage={page}
            limit={pageSize}
            totalItems={totalItems}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(newSize) => {
              setPageSize(newSize);
            }}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
            border={false}
          />
        </div>
      </div>

      {/* Form Modal */}
      {
        showForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
            <div className="bg-white rounded-lg shadow-xl w-11/12 md:w-3/4 lg:w-1/2 max-h-[90vh] flex flex-col">
              {/* Fixed Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? 'Edit Compliance' : 'Add Compliance'}
                </h3>
                <button
                  onClick={handleCloseForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Document Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.documentType}
                      onBlur={() => setTouched({ ...touched, documentType: true })}
                      onChange={(e) => {
                        const cleaned = handleTextInput(e.target.value, 'documentType', FIELD_LIMITS.documentType);
                        setFormData({ ...formData, documentType: cleaned });
                      }}
                      className={`input-field ${touched.documentType && formErrors.documentType ? 'border-red-500 bg-red-50' : ''}`}
                      placeholder="e.g., License, Certificate, Permit"
                      maxLength={FIELD_LIMITS.documentType}
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.documentType && formErrors.documentType && (
                          <p className="text-sm text-red-600">{formErrors.documentType}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-4">
                        {countCharacters(formData.documentType).total}/{FIELD_LIMITS.documentType}
                      </span>
                    </div>
                  </div>

                  {/* Document Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.documentName}
                      onBlur={() => setTouched({ ...touched, documentName: true })}
                      onChange={(e) => {
                        const cleaned = handleTextInput(e.target.value, 'documentName', FIELD_LIMITS.documentName);
                        setFormData({ ...formData, documentName: cleaned });
                      }}
                      className={`input-field ${touched.documentName && formErrors.documentName ? 'border-red-500 bg-red-50' : ''}`}
                      placeholder="Enter document name"
                      maxLength={FIELD_LIMITS.documentName}
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.documentName && formErrors.documentName && (
                          <p className="text-sm text-red-600">{formErrors.documentName}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-4">
                        {countCharacters(formData.documentName).total}/{FIELD_LIMITS.documentName}
                      </span>
                    </div>
                  </div>

                  {/* Document Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.documentNumber}
                      onBlur={() => setTouched({ ...touched, documentNumber: true })}
                      onChange={(e) => {
                        const cleaned = handleTextInput(e.target.value, 'documentNumber', FIELD_LIMITS.documentNumber);
                        setFormData({ ...formData, documentNumber: cleaned });
                      }}
                      className={`input-field ${touched.documentNumber && formErrors.documentNumber ? 'border-red-500 bg-red-50' : ''}`}
                      placeholder="Enter document number"
                      maxLength={FIELD_LIMITS.documentNumber}
                    />
                    <div className="flex justify-between items-start mt-1">
                      <div className="flex-1">
                        {touched.documentNumber && formErrors.documentNumber && (
                          <p className="text-sm text-red-600">{formErrors.documentNumber}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 ml-4">
                        {countCharacters(formData.documentNumber).total}/{FIELD_LIMITS.documentNumber}
                      </span>
                    </div>
                  </div>

                  {/* Issued Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Issued Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.issuedDate}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      onBlur={() => setTouched({ ...touched, issuedDate: true })}
                      onChange={(e) => {
                        const newIssuedDate = e.target.value;
                        setFormData({ ...formData, issuedDate: newIssuedDate });
                        if (formErrors.issuedDate) {
                          setFormErrors({ ...formErrors, issuedDate: '' });
                        }
                      }}
                      max={formData.expiryDate || undefined}
                      className={`input-field ${touched.issuedDate && formErrors.issuedDate ? 'border-red-500 bg-red-50' : ''}`}
                    />
                    {touched.issuedDate && formErrors.issuedDate && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.issuedDate}</p>
                    )}
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.expiryDate}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      onBlur={() => setTouched({ ...touched, expiryDate: true })}
                      onChange={(e) => {
                        setFormData({ ...formData, expiryDate: e.target.value });
                        if (formErrors.expiryDate) {
                          setFormErrors({ ...formErrors, expiryDate: '' });
                        }
                      }}
                      min={formData.issuedDate ? (() => {
                        const issued = new Date(formData.issuedDate);
                        issued.setDate(issued.getDate() + 1);
                        return issued.toISOString().split('T')[0];
                      })() : undefined}
                      className={`input-field ${touched.expiryDate && formErrors.expiryDate ? 'border-red-500 bg-red-50' : ''}`}
                    />
                    {touched.expiryDate && formErrors.expiryDate && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.expiryDate}</p>
                    )}
                  </div>

                  {/* Renewal Reminder Days */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Renewal Reminder Days (Optional)
                    </label>
                    <select
                      value={formData.renewalReminderDays}
                      onChange={(e) => setFormData({ ...formData, renewalReminderDays: parseInt(e.target.value) })}
                      className="input-field"
                    >
                      <option value={7}>7 days</option>
                      <option value={15}>15 days</option>
                      <option value={30}>30 days</option>
                      <option value={45}>45 days</option>
                      <option value={60}>60 days</option>
                      <option value={90}>90 days</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Number of days before expiry to send reminder
                    </p>
                  </div>

                  {/* File Upload section at the bottom */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document File <span className="text-red-500">*</span>
                    </label>

                    {formData.fileUrl ? (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="bg-green-100 p-2 rounded-lg">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Document Uploaded</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-[#0F2942]"
                              onClick={() => handleDownloadComplianceFile(formData)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={() => setShowFileDeleteConfirm(true)}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-blue-50', 'border-blue-300'); }}
                        onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300'); }}
                        onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-blue-50', 'border-blue-300'); handleFileChange(e); }}
                        onClick={() => fileInputRef.current?.click()}
                        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-200 ${formErrors.file ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                          }`}
                      >
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                        />

                        {uploadingFile ? (
                          <div className="flex flex-col items-center">
                            <Loader2 className="h-10 w-10 text-blue-500 animate-spin mb-2" />
                            <p className="text-sm font-medium text-gray-700">Uploading...</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            <Upload className="h-10 w-10 text-gray-400 mb-2" />
                            <p className="text-sm font-medium text-gray-700">Click to upload or drag and drop</p>
                            <p className="text-xs text-gray-500 mt-1">PDF, DOC, JPEG, PNG (Max 50MB)</p>
                          </div>
                        )}
                      </div>
                    )}
                    {formErrors.file && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.file}</p>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                    <button
                      type="button"
                      className="btn-cancel"
                      onClick={handleCloseForm}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-create flex items-center"
                      disabled={createMutation.isPending || updateMutation.isPending || !!uploadingFile || !isFormValid}
                    >
                      {createMutation.isPending || updateMutation.isPending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {editingId ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        editingId ? 'Update Compliance' : 'Create Compliance'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* View Detail Modal */}
      {
        showViewModal && selectedCompliance && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
              {/* Fixed Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-medium text-gray-900">Compliance Details</h3>
                <button
                  onClick={handleCloseViewModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Document Type */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Document Type
                    </label>
                    <p className="text-sm font-medium text-gray-900 capitalize break-words">
                      {selectedCompliance.documentType || 'N/A'}
                    </p>
                  </div>

                  {/* Document Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Document Name
                    </label>
                    <p className="text-sm text-gray-900 capitalize break-words">
                      {selectedCompliance.documentName || 'N/A'}
                    </p>
                  </div>

                  {/* Document Number */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Document Number
                    </label>
                    <p className="text-sm text-gray-900 uppercase break-words">
                      {selectedCompliance.documentNumber || 'N/A'}
                    </p>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Status
                    </label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedCompliance.badgeColor || 'bg-green-100 text-green-800'
                      }`}>
                      {selectedCompliance.statusText || 'Active'}
                    </span>
                  </div>

                  {/* Issued Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Issued Date
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedCompliance.issuedDate
                        ? formatDate(selectedCompliance.issuedDate, { includeTime: false })
                        : 'N/A'}
                    </p>
                  </div>

                  {/* Expiry Date */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Expiry Date
                    </label>
                    <p className="text-sm text-gray-900">
                      {selectedCompliance.expiryDate
                        ? formatDate(selectedCompliance.expiryDate, { includeTime: false })
                        : 'N/A'}
                    </p>
                  </div>

                  {/* Renewal Reminder Days */}
                  {selectedCompliance.renewalReminderDays && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Renewal Reminder Days
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedCompliance.renewalReminderDays} days
                      </p>
                    </div>
                  )}

                  {/* File */}
                  {selectedCompliance.fileUrl && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Document File
                      </label>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadComplianceFile(selectedCompliance)}
                          className="flex items-center space-x-2"
                        >
                          <Download className="h-4 w-4" />
                          <span>Download File</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseViewModal}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportPerPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Statutory Compliance Export"
      />
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['statutory-compliances'] })}
          config={{
            title: "Bulk Upload Statutory Compliance",
            subtitle: "Import multiple compliance documents using a CSV file",
            templateEndpoint: "/statutory-compliances/bulk-upload/template",
            validateEndpoint: "/statutory-compliances/bulk-upload/validate",
            uploadEndpoint: "/statutory-compliances/bulk-upload",
            templateFilename: "statutory_compliance_bulk_template.csv",
            dataKey: "data",
            maxRecords: 100,
            instructions: [
              "Document Type, Document Name, and Document Number are required.",
              "Document Number must be unique.",
              "Issued Date and Expiry Date are required (YYYY-MM-DD format).",
              "Expiry Date must be later than Issued Date.",
              "Renewal Reminder Days is optional (defaults to 15).",
              "File URL is required."
            ]
          }}
        />
      )}
    </div >
  );
};

export default StatutoryCompliance;
